import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AdminService } from '../../services/admin/admin.service';
import { IRestaurantInfo, RestaurantStatus } from '../../interfaces/restaurant';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, pluck, tap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { NotifierService } from 'angular-notifier';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../services/auth/auth.service';
import { nullIfEmptyValidator, wifiNameAndPasswordValidator } from '../../shared/custom-validators';
import { ConfirmationDialogComponent } from '../../components/modals/confirm-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Clipboard } from '@angular/cdk/clipboard';

type RestaurantInfoForm = {
  name: FormControl<string | null>;
  email: FormControl<string | null>;
  address: FormControl<string | null>;
  contactPhone: FormControl<string | null>;
  wifiName: FormControl<string | null>;
  wifiPassword: FormControl<string | null>;
  language: FormControl<string | null>;
  dwallGroupId: FormControl<string | number | null>;
  province: FormControl<string | null>;
  adRunEvery: FormControl<string | number | null>;
  adVistarMediaApiKey: FormControl<string | null>;
  adVistarMediaNetworkId: FormControl<string | null>;
  adVistarMediaVenueId: FormControl<string | null>;
  adVastTagUrl: FormControl<string | null>;
  status: FormControl<RestaurantStatus | null>;
  lastContactedAt: FormControl<Date | null>;
};

const compareObjects = (initialForm: FormGroup<RestaurantInfoForm>) =>
  Object.entries(initialForm.getRawValue())
    .filter(([key, value]) => initialForm?.get(key)?.dirty)
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

const parseDateOnly = (value: string | null): Date | null => {
  if (!value) {
    return null;
  }

  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDateOnly = (value: Date | string | null): string | null => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');

  return `${year}-${month}-${day}`;
};

@UntilDestroy()
@Component({
  selector: 'app-restaurant-account-page',
  templateUrl: './restaurant-account-page.component.html',
  styleUrls: ['./restaurant-account-page.component.scss']
})
export class RestaurantAccountPageComponent implements OnInit {

  public restaurantInfo: FormGroup<RestaurantInfoForm> = this.generateForm();
  public loading = false;
  public isAvailable = false;
  public restaurantId: number | null = null;
  public restaurantInfoData!: IRestaurantInfo;
  public language = localStorage.getItem('lang') ?? this.translate.currentLang;
  public publicUrl: string | null = null;
  public editable = false;
  public canViewRestrictedData = false;
  public canEditRestrictedData = false;
  public readonly restaurantStatuses: RestaurantStatus[] = ['active', 'inactive', 'test'];
  public maxLastContactedAt = new Date();

  constructor(
    private router: Router,
    private translate: TranslateService,
    private activateRoute: ActivatedRoute,
    private notifier: NotifierService,
    private matDialog: MatDialog,
    private adminService: AdminService,
    public auth: AuthService,
    private clipboard: Clipboard
  ) {
  }

  ngOnInit(): void {
    this.activateRoute.parent?.data.pipe(
      pluck('restaurant'),
      tap(restaurant => {
        this.restaurantId = restaurant.id;
        this.editable = this.auth.isSuperAdmin() || this.auth.isAdmin(restaurant);
        this.canViewRestrictedData = this.auth.isSuperAdmin() || this.auth.isViewer(restaurant);
        this.canEditRestrictedData = this.auth.isSuperAdmin();
        this.setFormAccess();
        this.publicUrl = restaurant.publicUrl;
        this.getRestaurantById(restaurant.id);
        this.isAvailable = this.auth.isSuperAdmin();
      }),
      untilDestroyed(this),
    ).subscribe(() => {
    });
  }

  private generateForm(): FormGroup<RestaurantInfoForm> {
    const urlRegex = new RegExp(
      '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR IP (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+()@]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~[\\]+=\\-/,]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$', // fragment locator
      'i'
    );
    const phoneRegex = new RegExp(/^\+?\d+$/);
    return new FormGroup({
      name: new FormControl<string | null>(''),
      email: new FormControl<string | null>(''),
      address: new FormControl<string | null>(''),
      contactPhone: new FormControl<string | null>('', [Validators.pattern(phoneRegex)]),
      wifiName: new FormControl<string | null>('', [Validators.maxLength(50)]),
      wifiPassword: new FormControl<string | null>('', [Validators.maxLength(64)]),
      language: new FormControl<string | null>(''),
      dwallGroupId: new FormControl<string | number | null>(''),
      province: new FormControl<string | null>(''),
      adRunEvery: new FormControl<string | number | null>('', [Validators.maxLength(255)]),
      adVistarMediaApiKey: new FormControl<string | null>('', [Validators.maxLength(255)]),
      adVistarMediaNetworkId: new FormControl<string | null>('', [Validators.maxLength(255)]),
      adVistarMediaVenueId: new FormControl<string | null>('', [Validators.maxLength(255)]),
      adVastTagUrl: new FormControl<string | null>(null, [Validators.pattern(urlRegex), nullIfEmptyValidator]),
      status: new FormControl<RestaurantStatus | null>(null, [Validators.minLength(1)]),
      lastContactedAt: new FormControl<Date | null>(null),
    }, {validators: [wifiNameAndPasswordValidator]});
  }

  private setFormAccess(): void {
    if (this.editable) {
      this.restaurantInfo.enable({emitEvent: false});
    } else {
      this.restaurantInfo.disable({emitEvent: false});
    }

    if (!this.canEditRestrictedData) {
      this.restaurantInfo.get('status')?.disable({emitEvent: false});
      this.restaurantInfo.get('lastContactedAt')?.disable({emitEvent: false});
    }
  }

  getRestaurantInfo(restaurant: IRestaurantInfo): void {
    this.restaurantInfo.patchValue({
      name: restaurant?.name,
      email: restaurant?.email,
      address: restaurant?.address,
      contactPhone: restaurant?.contactPhone,
      dwallGroupId: restaurant?.dwallGroupId,
      province: restaurant?.province,
      wifiName: restaurant?.wifiName,
      wifiPassword: restaurant?.wifiPassword,
      language: restaurant?.language,
      adRunEvery: restaurant?.adRunEvery,
      adVastTagUrl: restaurant?.adVastTagUrl,
      adVistarMediaApiKey: restaurant?.adVistarMediaApiKey,
      adVistarMediaNetworkId: restaurant?.adVistarMediaNetworkId,
      adVistarMediaVenueId: restaurant?.adVistarMediaVenueId,
      status: restaurant?.status,
      lastContactedAt: parseDateOnly(restaurant?.lastContactedAt),
    });
  }

  updateRestaurantInfo(form: FormGroup<RestaurantInfoForm>): void {
    this.loading = true;

    const changedValues: { [key: string]: any } = compareObjects(form);

    if (changedValues.hasOwnProperty('contactPhone')) {
      changedValues.contactPhone = changedValues.contactPhone.toString();
    }

    if (changedValues.hasOwnProperty('province') && changedValues.province === '') {
      changedValues.province = null;
    }

    if (changedValues.hasOwnProperty('dwallGroupId') && changedValues.dwallGroupId === '') {
      changedValues.dwallGroupId = null;
    }

    if (changedValues.hasOwnProperty('wifiName') && changedValues.wifiName === '') {
      changedValues.wifiName = null;
    }

    if (changedValues.hasOwnProperty('lastContactedAt')) {
      changedValues.lastContactedAt = formatDateOnly(changedValues.lastContactedAt);
    }

    if (!this.canEditRestrictedData) {
      delete changedValues.status;
      delete changedValues.lastContactedAt;
    }

    this.adminService.updateRestaurantInfo({ id: this.restaurantInfoData?.id, ...changedValues })
      .subscribe((restaurant) => {
      this.notifier.notify('success', this.translate.instant('pages.restaurantAccount.infoChangeSuccess'));
      this.loading = false;
      this.restaurantInfoData = restaurant;
      this.restaurantInfo.reset();
      this.getRestaurantInfo(restaurant);
    }, () => {
      this.loading = false;
    });
  }

  public getRestaurantById(restaurantId: number): void {
    this.adminService.getRestaurantInfo(restaurantId).subscribe((restaurant) => {
      this.restaurantInfoData = restaurant;
      this.getRestaurantInfo(restaurant);
    });
  }

  public clearLastContactedAt(): void {
    if (!this.canEditRestrictedData) {
      return;
    }

    const control = this.restaurantInfo.get('lastContactedAt');
    control?.setValue(null);
    control?.markAsDirty();
  }

  public confirmToRemoveRestaurant(): void {
    const title = this.translate.instant('pages.restaurantAccount.deleteRestaurantTitle');
    const message = this.translate.instant('pages.restaurantAccount.deleteRestaurant');

    const confirmationModal = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        message,
        title
      },
      panelClass: 'confirmation-modal'
    });

    confirmationModal.afterClosed().pipe(
      filter(Boolean),
    ).subscribe((): void => {
      this.removeRestaurant(this.restaurantInfoData?.id);
    });
  }

  public removeRestaurant(restaurantId: number): void {
    this.adminService.removeRestaurant(restaurantId).subscribe((data) => {

      const successMessage = `${this.translate.instant('pages.restaurantAccount.removedSuccessfullyPart1')} '${this.restaurantInfoData.name}' ${this.translate.instant('pages.restaurantAccount.removedSuccessfullyPart2')}`;
      this.notifier.notify('success', successMessage);

      this.router.navigate(['/chains', this.restaurantInfoData.chainId]);
    });
  }

  public downloadQr(): void {
    const parentElement = parent.document.getElementById('qrElement');
    const svgElement = parentElement?.querySelector('canvas')?.toDataURL('image/png');

    if (svgElement) {

      const blobData = this.convertBase64ToBlob(svgElement);

      const blob = new Blob([blobData], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      link.download = this.restaurantInfoData.name + 'PublicMenu';
      link.click();

      window.URL.revokeObjectURL(url);
    }
  }

  private convertBase64ToBlob(Base64Image: string): Blob {
    const parts = Base64Image.split(';base64,');
    const imageType = parts[0].split(':')[1];
    const decodedData = window.atob(parts[1]);
    const uInt8Array = new Uint8Array(decodedData.length);

    for (let i = 0; i < decodedData.length; ++i) {
      uInt8Array[i] = decodedData.charCodeAt(i);
    }

    return new Blob([uInt8Array], { type: imageType });
  }

  copyLink(): void {
    this.clipboard.copy(this.publicUrl as string);
    this.notifier.notify('success', this.translate.instant('pages.restaurantAccount.linkCopied'));
  }
}
